import { Component, Input, Output, EventEmitter, Inject, ViewChild, AfterViewInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { SubjectRelay } from '../../../../shared/commands/subject-relay.class';
import { EtatDocumentPa } from "../../entities/document-pa.entity";
import { DocumentPersonneAvertieResult } from "../../entities/document-personne-avertie-result";
import { PaSearchCriteria } from '../../entities/personne-avertie-search-criteria';
import { UserTLR } from '../../../humain/user/entities/user-tlr.entity';

import { DocumentPaService } from "../../services/document-pa-service";
import { UserService } from '../../../humain/user/services/user.service';

import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { JSR310ConversionService } from '../../../../shared/jsr310-localdates/services/jsr310-conversion.service';
import { LocalDate } from '../../../../shared/jsr310-localdates/types/local-date.type';

@Component({
    selector: 'liste-documents-pa',
    templateUrl: 'liste-documents-pa.html',
    styleUrls: ['liste-documents-pa.css']
})
export class ListeDocumentPaComponent implements AfterViewInit {

    isLoading: boolean = false;

    @Output() ajouterDocument = new EventEmitter<void>();
    @Output() modifierDocument = new EventEmitter<DocumentPersonneAvertieResult>();

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    displayedColumns: string[] = ['id', 'numeroPa', 'parcs', 'entreprises', 'description', 'etat', 'dateDebut', 'dateFin', 'validite', 'joursAvantExpiration', 'lien', 'actions'];

    data: DocumentPersonneAvertieResult[] = [];
    dataSource: MatTableDataSource<DocumentPersonneAvertieResult> = new MatTableDataSource(this.data);
    resumeDialogCommands: Subject<number> = new Subject<number>();
    generationCommands: Subject<number> = new Subject<number>();
    manageLinkCommands: Subject<DocumentPersonneAvertieResult> = new Subject<DocumentPersonneAvertieResult>();
    dialogSignatairesPaCommands: Subject<number> = new Subject<number>();
    manageIntervenantsCommands: Subject<number> = new Subject<number>();
	resumeEtat: Subject<string> = new Subject<string>();

    currentPa$ = new Subject<DocumentPersonneAvertieResult>();
    user!: UserTLR;


    private refreshCommandsSubject = new SubjectRelay<void>();
    @Input() set refreshCommands(obs: Observable<void> | undefined) {
        this.refreshCommandsSubject.registerSource(obs);
    }

    constructor(
        @Inject(DocumentPaService) private documentPaService: DocumentPaService,
        @Inject(JSR310ConversionService) private conversionService: JSR310ConversionService,
        @Inject(UserService) private userService: UserService) {

        this.refreshCommandsSubject.subscribe(
            () => {
                this.isLoading = true;
                // lors de la reception d'une demande de refresh
                // charger les données
                documentPaService.list().subscribe(
                    (docs: DocumentPersonneAvertieResult[]) => {
                        this.data = docs;
                        this.dataSource.data = docs;
                        this.dataSource._updateChangeSubscription();
                        this.isLoading = false;
                    },
                    (err: any) => {
                        console.error("Erreur lors du chargement des documents PA", err);
                        this.isLoading = false;
                    }
                );
            }
        );

        this.filters$.subscribe(
            (filters) => {

                if (this.user) {
                    filters.currentUserId = this.user.id;
                }

                this.dataSource.filter = JSON.stringify(filters);
            }
        );
    }

    private filters$ = new SubjectRelay<PaSearchCriteria>();
    @Input() set filtersSubject(obs: Observable<PaSearchCriteria> | undefined) {
        this.filters$.registerSource(obs);
    }

    filterPredicate(pa: DocumentPersonneAvertieResult, searchString: string): boolean {
        if (!searchString) return true;
        const searchCriteria = JSON.parse(searchString || '{}');

        // if no active filters, return everything
        const hasCriteria = Object.keys(searchCriteria).some(key => !!searchCriteria[key]);
        if (!hasCriteria) return true;

        let retainResult = true;

        if (
            (searchCriteria['etat'] && searchCriteria['etat'].length === 0) ||
            (searchCriteria['typePa'] && searchCriteria['typePa'].length === 0) ||
            (searchCriteria['validite'] && searchCriteria['validite'].length === 0)
        ) {
            return false;
        }

        // N° PA
        if (searchCriteria['numeroPa']) {
            retainResult = retainResult && pa.numeroPa?.toLowerCase().includes(searchCriteria['numeroPa'].toLowerCase());
        }

        // Mon périmètre référent
        if (searchCriteria.monPerimetreReferent === true) {
            retainResult = retainResult && pa.referentParcId === searchCriteria.currentUserId;
        }

        // Agence
        if (searchCriteria['agence']) {
            retainResult = retainResult && searchCriteria['agence'] === pa.agenceId;
        }

        // Antenne
        if (searchCriteria['antenne']) {
            retainResult = retainResult && searchCriteria['antenne'] === pa.antenneId;
        }

        // Groupement parc
        if (searchCriteria['groupement']) {
            retainResult = retainResult && pa.groupementIds?.includes(searchCriteria['groupement']);
        }

        // Parc
        if (searchCriteria['parc']) {
            retainResult = retainResult && pa.parcIds?.includes(searchCriteria['parc']);
        }

        // Entreprise
        if (searchCriteria['entreprise']) {
            if (pa.idEntreprises) {
                const entrepriseIds = pa.idEntreprises.split(',').map(Number);
                retainResult = retainResult && entrepriseIds.includes(searchCriteria['entreprise']);
            } else {
                return false;
            }
        }

        // Type PA, Sur site/A distance
        if (searchCriteria['typePa'] && searchCriteria['typePa'].length === 1) {
            retainResult = retainResult && (searchCriteria['typePa'].includes(pa.typeDocumentPa));
        }

        // Etat
        if (searchCriteria['etat'] && searchCriteria['etat'].length > 0) {
            const etatSelection: string[] = searchCriteria['etat'];
            const archiveSelected = etatSelection.includes(EtatDocumentPa.ARCHIVER);

            if (pa.obsolete) {
                // Only if "Archivé" is selected
                retainResult = retainResult && archiveSelected;
            } else {
                retainResult = retainResult && etatSelection.includes(pa.etat);
            }
        }

        // Validité
        if (searchCriteria['validite'] && searchCriteria['validite'].length > 0) {
            if (pa.dateFin && Array.isArray(pa.dateFin) && pa.dateFin.length >= 3) {
                const dateFin = new Date(pa.dateFin[0], pa.dateFin[1] - 1, pa.dateFin[2]);
                dateFin.setHours(23, 59, 59, 999);
                const nowInMillis = new Date().getTime();
                const validiteValue = nowInMillis > dateFin.getTime() ? 1 : 0;
                retainResult = retainResult && searchCriteria['validite'].includes(validiteValue);
            }
        }

        return retainResult;
    }

    ngAfterViewInit(): void {

        this.userService.getCurrentUserTLR().subscribe((user: UserTLR) => {

            this.user = user;

            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort;
            this.dataSource.filterPredicate = this.filterPredicate.bind(this);

            this.dataSource.sortingDataAccessor = (item: DocumentPersonneAvertieResult, property: string) => {
                switch (property) {
                    case 'id': return item.id;
                    case 'numeroPdp': return item.numeroPa;
                    case 'parcs': return item.parcDescription;
                    case 'entreprises': return item.nomEntreprises;
                    case 'description': return item.description;
                    case 'etat': return (item as any).etat;

                    case 'dateDebut': {
                        const localDateDebut = [item.dateDebut[0], item.dateDebut[1], item.dateDebut[2]] as LocalDate;
                        return this.conversionService.fromLocalDate(localDateDebut);
                    }

                    case 'dateFin':
                    case 'validite':
                    case 'joursAvantExpiration': {
                        const localDateFin = [item.dateFin[0], item.dateFin[1], item.dateFin[2]] as LocalDate;
                        return this.conversionService.fromLocalDate(localDateFin);
                    }

                    default:
                        return (item as { [key: string]: any })[property];
                }
            };

            this.refreshCommandsSubject.next();
        });
    }

    ajouterPa() {
        this.ajouterDocument.emit();
    }

    modifierPa(pa: DocumentPersonneAvertieResult) {
        this.modifierDocument.emit(pa);
        return false;
    }

    dupliquerPa(pa: DocumentPersonneAvertieResult) {
        pa.duplicatedId = pa.id;
        this.modifierDocument.emit(pa);
        return false;
    }

    export() {
        let exportCriteria: PaSearchCriteria = JSON.parse(this.dataSource.filter) as PaSearchCriteria;
		exportCriteria.archive = false;
		if (exportCriteria.etat.includes(EtatDocumentPa.ARCHIVER)) {
			exportCriteria.etat = exportCriteria.etat.filter( $ => $ !== EtatDocumentPa.ARCHIVER);
			exportCriteria.archive = true;
		}
        this.documentPaService.export(exportCriteria).subscribe(
            (blob: Blob) => {
                const objectUrl: string = URL.createObjectURL(blob);
                const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;
                a.href = objectUrl;
                a.download = 'export-pa.xls';
                document.body.appendChild(a);
                a.click();

                document.body.removeChild(a);
                URL.revokeObjectURL(objectUrl);
            },
            (err: any) => {
                alert("Erreur lors du téléchargement de l'export");
                console.log('Could not generate the file, error: ', err);
            }
        );
    }

    applyFilter(criteria: PaSearchCriteria) {
        this.dataSource.filterPredicate = (data: DocumentPersonneAvertieResult, filter: string) => {
            const parsed = JSON.parse(filter) as PaSearchCriteria;

            let match = true;

            if (parsed.numeroPa && parsed.numeroPa.trim() !== '') {
                match = match && data.numeroPa.toLowerCase().startsWith(parsed.numeroPa.toLowerCase());
            }

            if (parsed.monPerimetreReferent) {
                match = match && data.parcActif;
            }

            return match;
        };

        this.dataSource.filter = JSON.stringify(criteria);
    }

    showPaResume(pa: DocumentPersonneAvertieResult): boolean {
        this.resumeDialogCommands.next(pa.id);
		this.resumeEtat.next(pa.etat);
        return false;
    }

    openDialogForGeneration(pa: number): boolean {
        if (pa) {
            this.generationCommands.next(pa);
        }
        return false;
    }

    openDialogForPaLink(pa: DocumentPersonneAvertieResult): boolean {
        if (pa.parcActif) {
            this.manageLinkCommands.next(pa);
        }
        return false;
    }

    openDialogForIntervenants(pa: DocumentPersonneAvertieResult): boolean {
        if (pa.parcActif) {
            this.manageIntervenantsCommands.next(pa.id);
        }
        return false;
    }

    openSignatairesDialog(pa: DocumentPersonneAvertieResult): boolean {
        if (pa.parcActif) {
            this.dialogSignatairesPaCommands.next(pa.id);
        }
        return false;
    }

    archiver(pa: DocumentPersonneAvertieResult): boolean {
        if (!pa.obsolete) {
            if (
                !confirm('Confirmez-vous l\'archivage du PA N°' + pa.numeroPa + ' pour le parc ' + pa.parcDescription + ' ?')
            ) {
                return false;
            }
        } else {
            if (!confirm('Voulez-vous désarchiver le PA ?')) {
                return false;
            }
        }

        this.isLoading = true;

        this.documentPaService.archive(pa.id).subscribe(
            () => {
                // rechargement de la liste des PA
                this.refreshCommandsSubject.next();
            },
            (err) => {
                console.error('Erreur lors de l’archivage / désarchivage du PA', err);
                this.isLoading = false;
            }
        );

        return false;
    }

    supprimer(pa: DocumentPersonneAvertieResult): boolean {
        if (
            !confirm('Confirmez-vous la suppression du PA N°' + pa.numeroPa + ' pour le parc ' + pa.parcDescription + ' ?')
        ) {
            return false;
        }

        this.isLoading = true;

        this.documentPaService.supprimer(pa.id).subscribe(
            () => {
                this.refreshCommandsSubject.next();
            },
            (err) => {
                console.error('Erreur lors de la suppression du PA', err);
                this.isLoading = false;
            }
        );

        return false;
    }

    onPaLinkChanged(paId: number): void {
        this.refreshCommandsSubject.next();
    }

    onPaSignatureChanged(paId: number): void {
        this.refreshCommandsSubject.next();
    }


}